import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Collapse,
  Paper,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import MessageTemplateConfig from "./MessageTemplateConfig";
import ButtonTemplateConfig from "./ButtonTemplateConfig";
import ButtonTemplatePreview from "./ButtonTemplatePreview";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toast } from "react-toastify";
import axios from "axios";

const InstagramScheduler = ({ token, pageId, onScheduled, showHeader = false }) => {
  const [caption, setCaption] = useState("");
  const [mediaUrls, setMediaUrls] = useState([]);
  const [mediaType, setMediaType] = useState("IMAGE");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [scheduledTime, setScheduledTime] = useState(
    new Date(Date.now() + 24 * 60 * 60 * 1000)
  ); // Default to tomorrow
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  // Auto DM Configuration state
  const [autoDMExpanded, setAutoDMExpanded] = useState(false);
  const [autoDMTemplateType, setAutoDMTemplateType] = useState(
    "ButtonTemplate"
  );
  const [autoDMConfig, setAutoDMConfig] = useState({
    commentKeywords: [],
    templateMessage: "",
    cardTemplates: [],
    isEnabled: false,
    templateType: "ButtonTemplate",
  });

  useEffect(() => {
    if (pageId) {
      fetchScheduledPosts();
    }
  }, [pageId]);

  // Auto-expand Auto DM section when enabled
  useEffect(() => {
    if (autoDMConfig.isEnabled && !autoDMExpanded) {
      setAutoDMExpanded(true);
    }
  }, [autoDMConfig.isEnabled, autoDMExpanded]);

  const fetchScheduledPosts = async () => {
    setLoadingPosts(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/user-scheduled-posts/${pageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setScheduledPosts(response.data);
    } catch (error) {
      console.error("Error fetching scheduled posts:", error);
      toast.error("Failed to load scheduled posts");
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Check file size limits (Cloudinary limit of 100MB for video)
    for (const file of files) {
      if (file.type.startsWith("video/") && file.size > 100 * 1024 * 1024) {
        toast.error("Video size exceeds the 100MB limit. Please upload a smaller video.");
        return;
      }
    }

    setUploadingMedia(true);
    try {
      const uploadedUrls = [];

      for (const file of files) {
        // Detect if file is a video or image
        const isVideo = file.type.startsWith("video/");
        const uploadType = isVideo ? "video" : "image";
        
        const formData = new FormData();
        formData.append("file", file);
        formData.append(
          "upload_preset",
          process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
        );

        // Use the correct Cloudinary endpoint based on file type
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/${uploadType}/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error?.message || `Failed to upload ${uploadType}`
          );
        }

        const data = await response.json();
        uploadedUrls.push(data.secure_url);
      }

      setMediaUrls((prev) => [...prev, ...uploadedUrls]);
      const mediaType = files.some((f) => f.type.startsWith("video/"))
        ? "video(s)"
        : "image(s)";
      toast.success(`${files.length} ${mediaType} uploaded successfully`);
    } catch (error) {
      console.error("Error uploading media:", error);
      toast.error(
        error.message || "Failed to upload media. Please try again."
      );
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleRemoveMedia = (index) => {
    setMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleThumbnailUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate it's an image
    if (!file.type.startsWith("image/")) {
      toast.error("Thumbnail must be an image file");
      return;
    }

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append(
        "upload_preset",
        process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
      );

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload thumbnail");
      }

      const data = await response.json();
      setThumbnailUrl(data.secure_url);
      toast.success("Thumbnail uploaded successfully");
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      toast.error("Failed to upload thumbnail");
    } finally {
      setUploadingThumbnail(false);
      // Reset the input so the same file can be selected again
      event.target.value = "";
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailUrl("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!caption.trim()) {
      toast.error("Please enter a caption");
      return;
    }

    if (!mediaUrls || mediaUrls.length === 0) {
      toast.error("Please upload at least one media file");
      return;
    }

    if (scheduledTime <= new Date()) {
      toast.error("Scheduled time must be in the future");
      return;
    }

    // Validate Auto DM config if enabled
    if (autoDMConfig.isEnabled) {
      if (
        !autoDMConfig.commentKeywords ||
        autoDMConfig.commentKeywords.length === 0
      ) {
        toast.error(
          "Please add at least one keyword when Auto DM is enabled"
        );
        setAutoDMExpanded(true); // Expand section to show the error
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const requestBody = {
        caption,
        mediaUrls,
        mediaType,
        thumbnailUrl: mediaType === "VIDEO" ? thumbnailUrl : "",
        scheduledTime: scheduledTime.toISOString(),
      };

      // Include autoCommentConfig if it exists and is enabled
      if (autoDMConfig.isEnabled) {
        requestBody.autoCommentConfig = autoDMConfig;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/schedule-post/${pageId}`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Post scheduled successfully!");
      setCaption("");
      setMediaUrls([]);
      setThumbnailUrl("");
      setScheduledTime(new Date(Date.now() + 24 * 60 * 60 * 1000));
      fetchScheduledPosts(); // Refresh the list
      console.log("Calling onScheduled callback...");
      if (onScheduled) {
        await onScheduled();
        console.log("onScheduled callback completed");
      }
    } catch (error) {
      console.error("Error scheduling post:", error);
      toast.error(error.response?.data?.error || "Failed to schedule post");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/scheduled-post/${postId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Scheduled post deleted");
      fetchScheduledPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete scheduled post");
    }
  };

  const handlePublishNow = async (postId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/instagram/publish-scheduled-post/${postId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Post published successfully!");
      fetchScheduledPosts();
    } catch (error) {
      console.error("Error publishing post:", error);
      toast.error("Failed to publish post");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "PUBLISHED":
        return "success";
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="mt-6">
        {/* Header with title and Schedule Post button */}
        {showHeader && (
          <div className="flex items-center justify-between mb-6">
            <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
              Schedule Instagram Post
            </Typography>
            <Button
              variant="contained"
              startIcon={<ScheduleIcon />}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e);
              }}
              disabled={isSubmitting || uploadingMedia}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
            >
              {isSubmitting ? "Scheduling..." : "Schedule Post"}
            </Button>
          </div>
        )}

        <div className="card-modern p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-6">
            {/* Left: Form */}
            <div className="md:col-span-4">
                <form onSubmit={handleSubmit} id="schedule-form" className="space-y-4">
                  <div>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Caption"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Write your Instagram caption here..."
                      helperText={`${caption.length}/2200 characters`}
                      inputProps={{ maxLength: 2200 }}
                      className="bg-white dark:bg-gray-800"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '0.75rem',
                        },
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormControl fullWidth>
                      <InputLabel className="text-gray-700 dark:text-gray-300">Media Type</InputLabel>
                      <Select
                        value={mediaType}
                        onChange={(e) => {
                          setMediaType(e.target.value);
                          // Clear thumbnail when switching away from VIDEO
                          if (e.target.value !== "VIDEO") {
                            setThumbnailUrl("");
                          }
                        }}
                        label="Media Type"
                        className="bg-white dark:bg-gray-800"
                      >
                        <MenuItem value="IMAGE">Single Image</MenuItem>
                        <MenuItem value="VIDEO">Video</MenuItem>
                        <MenuItem value="CAROUSEL_ALBUM">
                          Carousel (Multiple Images)
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <DateTimePicker
                      label="Schedule Time"
                      value={scheduledTime}
                      onChange={setScheduledTime}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          fullWidth 
                          className="bg-white dark:bg-gray-800"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '0.75rem',
                            },
                          }}
                        />
                      )}
                      minDateTime={new Date()}
                    />
                  </div>

                  <div>
                    <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Media Upload
                    </Typography>
                        <input
                          accept={mediaType === "VIDEO" ? "video/*" : "image/*"}
                          style={{ display: "none" }}
                          id="media-upload"
                          multiple={mediaType === "CAROUSEL_ALBUM"}
                          type="file"
                          onChange={handleMediaUpload}
                        />
                        <label htmlFor="media-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            disabled={uploadingMedia}
                            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 normal-case"
                          >
                            {uploadingMedia ? "Uploading..." : "Upload Media"}
                          </Button>
                        </label>
                  </div>

                  {/* Thumbnail upload for VIDEO type */}
                  {mediaType === "VIDEO" && (
                    <div className="mb-4">
                      <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Video Thumbnail (Optional)
                      </Typography>
                      <Typography
                        variant="caption"
                        className="text-sm text-gray-600 dark:text-gray-400 block mb-3"
                      >
                        Upload a custom thumbnail image for your video
                      </Typography>
                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="thumbnail-upload"
                        type="file"
                        onChange={handleThumbnailUpload}
                      />
                      <label htmlFor="thumbnail-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          disabled={uploadingThumbnail}
                          className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 normal-case mr-2"
                        >
                          {uploadingThumbnail
                            ? "Uploading..."
                            : thumbnailUrl
                            ? "Change Thumbnail"
                            : "Upload Thumbnail"}
                        </Button>
                      </label>
                      {thumbnailUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={handleRemoveThumbnail}
                          className="border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 normal-case"
                        >
                          Remove
                        </Button>
                      )}
                      {thumbnailUrl && (
                        <div className="mt-4">
                          <img
                            src={thumbnailUrl}
                            alt="Thumbnail preview"
                            className="max-w-[200px] max-h-[200px] object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>

              {/* Right: Preview */}
              <div className="md:col-span-3">
                <div className="card-modern p-4 h-full">
                  <Typography variant="subtitle1" className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Preview
                  </Typography>
                  <Typography
                    variant="caption"
                    className="text-xs text-gray-600 dark:text-gray-400 block mb-4"
                  >
                    Scheduled for: {scheduledTime.toLocaleString()}
                  </Typography>
                  <div className="mt-4">
                    {mediaUrls.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {mediaUrls.map((url, index) => (
                          <div key={index} className="relative">
                            {mediaType === "VIDEO" ? (
                              <div className="w-[140px] h-[140px] rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gray-100 dark:bg-gray-800 relative">
                                {thumbnailUrl ? (
                                  <img
                                    src={thumbnailUrl}
                                    alt={`Video thumbnail ${index + 1}`}
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                ) : (
                                  <Typography
                                    variant="caption"
                                    className="text-gray-500 dark:text-gray-400"
                                  >
                                    Video
                                  </Typography>
                                )}
                              </div>
                            ) : (
                              <img
                                src={url}
                                alt={`Media ${index + 1}`}
                                className="w-[140px] h-[140px] object-cover rounded-lg"
                              />
                            )}
                            <IconButton
                              size="small"
                              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white"
                              onClick={() => handleRemoveMedia(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                        No media selected yet.
                      </Typography>
                    )}

                    <div className="mt-4">
                      <Typography variant="subtitle2" className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Caption Preview
                      </Typography>
                      <Typography
                        variant="body2"
                        className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                      >
                        {caption || "Your caption will appear here..."}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Auto DM Configuration Section - Full Width */}
        <div className="card-modern p-6 mb-6">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() =>
              setAutoDMExpanded(!autoDMExpanded)
            }
          >
            <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white">
              Auto DM for Comments Bot
            </Typography>
            <IconButton size="small" className="text-gray-700 dark:text-gray-300">
              {autoDMExpanded ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          </div>

          <Collapse in={autoDMExpanded}>
            <div>
              <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>
              <div className="flex justify-center mb-4">
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={autoDMTemplateType}
                  onChange={(_, v) => {
                    if (v) {
                      setAutoDMTemplateType(v);
                      setAutoDMConfig((prev) => ({
                        ...prev,
                        templateType: v,
                      }));
                    }
                  }}
                  className="bg-gray-100 dark:bg-gray-800"
                >
                  <ToggleButton 
                    value="ButtonTemplate"
                    className={`normal-case ${
                      autoDMTemplateType === "ButtonTemplate"
                        ? "bg-primary-600 text-white"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    Button Template
                  </ToggleButton>
                  <ToggleButton 
                    value="MessageTemplate"
                    className={`normal-case ${
                      autoDMTemplateType === "MessageTemplate"
                        ? "bg-primary-600 text-white"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    Message Template
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <div className="overflow-visible min-h-0">
                {autoDMTemplateType === "MessageTemplate" ? (
                  <MessageTemplateConfig
                    token={token}
                    postId={null}
                    onClose={() => {}}
                    controlled={true}
                    value={autoDMConfig}
                    onChange={setAutoDMConfig}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-8">
                      <ButtonTemplateConfig
                        token={token}
                        postId={null}
                        pageId={pageId}
                        onClose={() => {}}
                        controlled={true}
                        value={autoDMConfig}
                        onChange={setAutoDMConfig}
                        caption={caption}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <div className="flex justify-center items-start pt-2">
                        <ButtonTemplatePreview
                          cardTemplates={
                            autoDMConfig.cardTemplates || []
                          }
                          activeTemplateIndex={0}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>

        {/* Scheduled posts list removed; shown by calendar page */}
      </div>
    </LocalizationProvider>
  );
};

export default InstagramScheduler;
